/* ============================================================
 * 工单 store
 *
 * 设计：
 *   - 详情按 orderNo 缓存（Map），不做 TTL：状态会变，UI 主动调 `refresh()`
 *   - 草稿空态默认值（2099-12-31 / '' / 0）在 PublishView 层识别，
 *     store 只负责读写原始 VO，不做语义转换。
 * ============================================================ */

import { ref } from 'vue'
import { defineStore } from 'pinia'
import { getOrderDetail } from '@/api/order'
import type { OrderDetailVO } from '@/types/order'

export const useOrderStore = defineStore('order', () => {
  const detailMap = ref<Record<string, OrderDetailVO>>({})
  const loadingOrderNo = ref<string | null>(null)
  const errorMessage = ref('')

  function getDetail(orderNo: string): OrderDetailVO | null {
    return detailMap.value[orderNo] ?? null
  }

  async function loadDetail(orderNo: string, force = false): Promise<OrderDetailVO | null> {
    if (loadingOrderNo.value === orderNo) return detailMap.value[orderNo] ?? null
    if (!force && detailMap.value[orderNo]) return detailMap.value[orderNo]

    loadingOrderNo.value = orderNo
    errorMessage.value = ''
    try {
      const resp = await getOrderDetail(orderNo)
      detailMap.value[orderNo] = resp.data
      return resp.data
    } catch (err) {
      errorMessage.value = err instanceof Error ? err.message : '加载订单详情失败'
      return null
    } finally {
      loadingOrderNo.value = null
    }
  }

  function refresh(orderNo: string): Promise<OrderDetailVO | null> {
    return loadDetail(orderNo, true)
  }

  function patchDetail(orderNo: string, patch: Partial<OrderDetailVO>): void {
    const current = detailMap.value[orderNo]
    if (!current) return
    detailMap.value[orderNo] = { ...current, ...patch }
  }

  function evict(orderNo: string): void {
    delete detailMap.value[orderNo]
  }

  function reset(): void {
    detailMap.value = {}
    loadingOrderNo.value = null
    errorMessage.value = ''
  }

  return {
    detailMap,
    loadingOrderNo,
    errorMessage,
    getDetail,
    loadDetail,
    refresh,
    patchDetail,
    evict,
    reset,
  }
})
